// XLMoneyInput - Sprint 45 (entregado en S44 Fase 1 XL).
// Input de dinero con formato universal: "." miles, "," decimales,
// 2 decimales fijos. Reusa src/utils/money.js (fmtNumber + parseMoney).
//
// Contrato:
//   - value: numero (lo que el padre guarda). Puede ser null/undefined.
//   - onChange(number): notifica nuevo valor. SIEMPRE numero.
//   - moneda: string opcional (ej. 'COP'). Si va, se muestra como prefijo.
//   - align: 'right' (default, tipico para tablas) | 'left'.
//   - Al FOCUS muestra el numero crudo editable (ej. '100000').
//   - Al BLUR muestra formateado con miles/decimales (ej. '100.000,00').
//
// Compat con forwardRef para formularios. aria-invalid automatico con error.
import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import { fmtNumber, parseMoney } from '../../utils/money';

const CSS_ID = 'xl-money-css';
const CSS = `
.xl-money-wrap {
  display: inline-flex;
  align-items: stretch;
  width: 100%;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: 8px;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow   120ms cubic-bezier(0,0,0.2,1);
  font-family: var(--xl-font-sans);
  box-sizing: border-box;
}
.xl-money-wrap:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-money-wrap.xl-money-wrap--focus {
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
}
.xl-money-wrap.xl-money-wrap--error {
  border-color: var(--xl-color-danger);
}
.xl-money-wrap.xl-money-wrap--error.xl-money-wrap--focus {
  box-shadow: 0 0 0 3px rgba(220,38,38,0.22);
}
.xl-money-wrap.xl-money-wrap--disabled {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-muted);
  cursor: not-allowed;
}
[data-xl-theme="dark"] .xl-money-wrap--focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}

.xl-money__prefix {
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-secondary);
  font-family: var(--xl-font-mono);
  font-size: 12px;
  font-weight: 600;
  border-right: 1px solid var(--xl-color-border);
  border-top-left-radius: var(--xl-radius-md);
  border-bottom-left-radius: var(--xl-radius-md);
}

.xl-money__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--xl-color-text);
  padding: 10px 12px;
  font: 400 14px/1.4 var(--xl-font-mono);
  text-align: right;
  box-sizing: border-box;
}
.xl-money__input--left { text-align: left }
.xl-money__input:disabled { cursor: not-allowed; color: var(--xl-color-text-muted) }
.xl-money__input::placeholder { color: var(--xl-color-text-muted) }
`;

function _displayOf(value) {
  // Blur-mode: formateado con miles/decimales o vacio.
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return fmtNumber(n);
}

function _rawOf(value) {
  // Focus-mode: numero plano editable (sin separador miles).
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  // Evitar notacion cientifica para numeros normales; mantener decimales.
  return n.toString();
}

const XLMoneyInput = forwardRef(function XLMoneyInput({
  label,
  value,
  onChange,
  moneda,
  align = 'right',
  hint,
  error,
  id,
  className = '',
  disabled = false,
  placeholder = '0,00',
  ...rest
}, ref) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const autoId = useId();
  const fieldId = id || ('xl-money-' + autoId);

  const [focused, setFocused] = useState(false);
  const [draft, setDraft]     = useState('');
  const innerRef = useRef(null);
  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  // Mostramos draft si estamos en focus; si no, formateado desde prop.
  const displayValue = focused ? draft : _displayOf(value);

  const handleFocus = (e) => {
    setFocused(true);
    setDraft(_rawOf(value));
    if (rest.onFocus) rest.onFocus(e);
  };
  const handleChange = (e) => {
    setDraft(e.target.value);
  };
  const handleBlur = (e) => {
    setFocused(false);
    const parsed = parseMoney(draft);
    setDraft('');
    if (typeof onChange === 'function') onChange(parsed);
    if (rest.onBlur) rest.onBlur(e);
  };

  const wrapCls = [
    'xl-money-wrap',
    focused ? 'xl-money-wrap--focus' : '',
    error ? 'xl-money-wrap--error' : '',
    disabled ? 'xl-money-wrap--disabled' : '',
  ].filter(Boolean).join(' ');

  const inputCls = 'xl-money__input ' + (align === 'left' ? 'xl-money__input--left' : '');

  return (
    <label className={'xl-field ' + className} htmlFor={fieldId}>
      {label && <span className="xl-field__label">{label}</span>}
      <span className={wrapCls}>
        {moneda && <span className="xl-money__prefix">{moneda}</span>}
        <input
          {...rest}
          id={fieldId}
          ref={setRefs}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={inputCls}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            error ? fieldId + '-err' : hint ? fieldId + '-hint' : undefined
          }
        />
      </span>
      {!error && hint && (
        <span id={fieldId + '-hint'} className="xl-field__hint">{hint}</span>
      )}
      {error && (
        <span id={fieldId + '-err'} className="xl-field__error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
});

export default XLMoneyInput;
